import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { TestService } from '../../services/test.service';
import { ComparaisonService } from '../../services/comparaison.service';

interface ComparisonResult {
  row_number: number;
  status: string;
  data: any;
  errors?: string[];
  warnings?: string[];
  comparison_results?: any;
}

interface TestResult {
  success: boolean;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  results: ComparisonResult[];
  cleaned_data?: any[];
  summary?: {
    success_count: number;
    error_count: number;
    warning_count: number;
    success_rate: number;
  };
}

@Component({
  selector: 'app-test-upload',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FileUploadModule,
    ButtonModule,
    CardModule,
    TableModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    ToastModule
  ],
  providers: [],
  templateUrl: './test-upload.component.html',
  styleUrls: ['./test-upload.component.scss']
})
export class TestUploadComponent implements OnInit {
  uploadForm: FormGroup;
  isLoading = false;
  uploadedFile: File | null = null;
  testResults: TestResult | null = null;
  showResults = false;
  processingMode: 'manual' | 'ai' | 'export' = 'export'; // Par défaut mode Export
  exportFormat: 'excel' | 'csv' = 'excel';
  
  // Pour le traitement AI + comparaisons
  aiProcessedData: any[] = [];
  comparisonProgress = 0;
  comparisonResults: any[] = [];
  isComparingData = false;

  constructor(
    private fb: FormBuilder,
    private testService: TestService,
    private comparaisonService: ComparaisonService,
    private messageService: MessageService
  ) {
    this.uploadForm = this.fb.group({
      file: [null, Validators.required]
    });
  }

  ngOnInit(): void {}

  onFileSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      // Vérifier l'extension du fichier
      const allowedExtensions = ['csv', 'xlsx', 'xls'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension && allowedExtensions.includes(fileExtension)) {
        this.uploadedFile = file;
        this.uploadForm.patchValue({ file: file });
        this.messageService.add({
          severity: 'success',
          summary: 'Fichier sélectionné',
          detail: `${file.name} est prêt à être testé`
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Veuillez sélectionner un fichier CSV ou Excel (.csv, .xlsx, .xls)'
        });
      }
    }
  }

  onFileRemove(): void {
    this.uploadedFile = null;
    this.uploadForm.patchValue({ file: null });
    this.showResults = false;
    this.testResults = null;
  }

  async onSubmit(): Promise<void> {
    if (!this.uploadedFile || this.uploadForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez sélectionner un fichier'
      });
      return;
    }

    if (this.processingMode === 'export') {
      await this.processAndExport();
    } else if (this.processingMode === 'ai') {
      await this.processWithAI();
    } else {
      await this.processManually();
    }
  }

  async processAndExport(): Promise<void> {
    this.isLoading = true;

    try {
      const formData = new FormData();
      formData.append('file', this.uploadedFile!);

      this.messageService.add({
        severity: 'info',
        summary: 'Traitement en cours',
        detail: 'L\'IA nettoie vos données...'
      });

      // Appeler l'API pour nettoyer et exporter
      const blob = await this.testService.cleanAndExport(formData, this.exportFormat);

      // Télécharger le fichier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `donnees_nettoyees_${Date.now()}.${this.exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      link.click();
      window.URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: 'Export réussi',
        detail: `Fichier ${this.exportFormat.toUpperCase()} téléchargé avec succès`
      });
    } catch (error: any) {
      console.error('Erreur export:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: error.error?.detail || 'Une erreur est survenue lors de l\'export'
      });
    } finally {
      this.isLoading = false;
    }
  }

  async processWithAI(): Promise<void> {
    this.isLoading = true;
    this.showResults = false;
    this.aiProcessedData = [];
    this.comparisonResults = [];

    try {
      const formData = new FormData();
      formData.append('file', this.uploadedFile!);

      // Étape 1: Traitement IA
      this.messageService.add({
        severity: 'info',
        summary: 'Traitement en cours',
        detail: 'L\'IA analyse et nettoie vos données...'
      });

      const aiResult = await this.testService.aiProcessFile(formData);

      if (aiResult.success && aiResult.processed_data.length > 0) {
        this.aiProcessedData = aiResult.processed_data;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Nettoyage terminé',
          detail: `${aiResult.total_rows} lignes nettoyées par l'IA. Début des comparaisons...`
        });

        // Étape 2: Faire les comparaisons
        await this.performComparisons();
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: aiResult.errors?.join(', ') || 'Aucune donnée traitée'
        });
      }
    } catch (error: any) {
      console.error('Erreur lors du traitement IA:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: error.error?.detail || 'Une erreur est survenue lors du traitement IA'
      });
    } finally {
      this.isLoading = false;
    }
  }

  async performComparisons(): Promise<void> {
    this.isComparingData = true;
    this.comparisonProgress = 0;
    const totalRows = this.aiProcessedData.length;

    for (let i = 0; i < totalRows; i++) {
      try {
        const rowData = this.aiProcessedData[i];
        
        // Appel de la méthode de comparaison
        const comparisonResult = await this.comparaisonService.compareWithAllProviders(rowData);
        
        this.comparisonResults.push({
          row_number: i + 1,
          status: 'success',
          data: rowData,
          comparison_results: comparisonResult
        });
      } catch (error: any) {
        console.error(`Erreur ligne ${i + 1}:`, error);
        this.comparisonResults.push({
          row_number: i + 1,
          status: 'error',
          data: this.aiProcessedData[i],
          errors: [error.message || 'Erreur de comparaison']
        });
      }

      // Mise à jour de la progression
      this.comparisonProgress = Math.round(((i + 1) / totalRows) * 100);
    }

    this.isComparingData = false;
    this.showResults = true;

    // Créer un résumé des résultats
    this.testResults = {
      success: true,
      total_rows: totalRows,
      valid_rows: this.comparisonResults.filter(r => r.status === 'success').length,
      invalid_rows: this.comparisonResults.filter(r => r.status === 'error').length,
      results: this.comparisonResults
    };

    this.messageService.add({
      severity: 'success',
      summary: 'Comparaisons terminées',
      detail: `${this.testResults.valid_rows}/${totalRows} comparaisons réussies`
    });
  }

  async processManually(): Promise<void> {
    this.isLoading = true;
    this.showResults = false;

    try {
      const formData = new FormData();
      formData.append('file', this.uploadedFile!);

      this.testResults = await this.testService.uploadAndTest(formData);
      this.showResults = true;

      if (this.testResults.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Test réussi',
          detail: `${this.testResults.valid_rows}/${this.testResults.total_rows} lignes valides`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Test terminé avec des erreurs',
          detail: `${this.testResults.invalid_rows}/${this.testResults.total_rows} lignes invalides`
        });
      }
    } catch (error: any) {
      console.error('Erreur lors du test:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: error.error?.detail || 'Une erreur est survenue lors du test'
      });
    } finally {
      this.isLoading = false;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'valid':
        return 'status-valid';
      case 'invalid':
        return 'status-invalid';
      case 'warning':
        return 'status-warning';
      default:
        return '';
    }
  }

  downloadCleanedData(): void {
    if (!this.testResults?.cleaned_data) return;

    const blob = new Blob([JSON.stringify(this.testResults.cleaned_data, null, 2)], {
      type: 'application/json'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cleaned_data.json';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  reset(): void {
    this.uploadedFile = null;
    this.testResults = null;
    this.showResults = false;
    this.uploadForm.reset();
    this.aiProcessedData = [];
    this.comparisonResults = [];
    this.comparisonProgress = 0;
  }

  toggleProcessingMode(): void {
    const modes: Array<'export' | 'ai' | 'manual'> = ['export', 'ai', 'manual'];
    const currentIndex = modes.indexOf(this.processingMode);
    this.processingMode = modes[(currentIndex + 1) % modes.length];
    
    const modeLabels = {
      'export': '📥 Export Nettoyé',
      'ai': '🤖 IA + Comparaisons',
      'manual': '📋 Manuel'
    };
    
    this.messageService.add({
      severity: 'info',
      summary: 'Mode changé',
      detail: modeLabels[this.processingMode]
    });
  }

  toggleExportFormat(): void {
    this.exportFormat = this.exportFormat === 'excel' ? 'csv' : 'excel';
  }
}
